/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useEffect, useState } from 'react';

// Lib dependencies
import {
	ActionIcon,
	Drawer,
	Flex,
	RangeSlider,
	Stack,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';

// Styles
import classes from './Filter.module.css';

// UI local components
import { Button, Text, Title } from '@components/v2/ui';

// Icons
import { IconX } from '@tabler/icons-react';

// Utils
import { DatePickerInput } from '@components/v2/ui/DatePickerInput/DatePickerInput';
import { EUROPEAN_COUNTRIES } from '@config/constants/european-countries';
import { cloneDeep } from '@utils/functions';
import { getUnitLabel } from '@utils/units';
import dayjs from 'dayjs';
import isNil from 'lodash/isNil';
import { useCountryCodes } from 'shared/api/get-country-codes';

/* -------------------------------------------------------------------------- */
/*                                   Types                                     */
/* -------------------------------------------------------------------------- */

export type RangeFilter = {
	$gte: number;
	$lte: number;
};

export type QueryParams = {
	'location.countryCode'?: { $in: string[] };
	technology?: string[];
	sellPrice_europertco2eq?: RangeFilter;
	'quantity.remaining_aco2'?: RangeFilter;
	[key: string]: unknown;
};

type TFilterOffsetsProps = {
	opened: boolean;
	onClose: () => void;
	title: string;
	onApplyFilters: (filters: QueryParams) => void;
	value: QueryParams;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

export const Filter = ({
	opened,
	onClose,
	title,
	onApplyFilters,
	value,
}: TFilterOffsetsProps) => {
	/* ******************************** HOOKS ******************************* */
	const { t } = useTranslation();
	const [
		valuePricePerTonCO2Slider,
		setPricePerTonCO2ValueSlider,
	] = useState<[number, number]>([0, 100]);
	const [valueAvailableStock, setValueAvailableStock] =
		useState<[number, number]>([0, 20000]);

	const [queryParams, setQueryParams] =
		useState<QueryParams>(value ?? {});

	/* ******************************** HELPERS ******************************* */

	const handleReset = () => {
		setPricePerTonCO2ValueSlider([0, 100]);
		setValueAvailableStock([0, 20000]);
		setQueryParams({});
		onApplyFilters({});
		onClose();
	};

	const { data: countryCodes } = useCountryCodes(
		'measurabilities'
	);
	const filteredCountries =
		!isNil(countryCodes) && Array.isArray(countryCodes)
			? EUROPEAN_COUNTRIES.filter((item) =>
					countryCodes.includes(item.value)
				)
			: [];

	const handleApplyFilters = () => {
		onApplyFilters(queryParams);
		onClose();
	};

	useEffect(() => {
		if (opened) {
			setQueryParams(cloneDeep(value ?? {}));

			const priceFilter = value?.sellPrice_europertco2eq;
			setPricePerTonCO2ValueSlider([
				priceFilter?.$gte ?? 0,
				priceFilter?.$lte ?? 100,
			]);

			const stockFilter =
				value?.['quantity.remaining_aco2'];
			setValueAvailableStock([
				stockFilter?.$gte ?? 0,
				stockFilter?.$lte ?? 20000,
			]);
		}
	}, [opened, value]);

	/* ******************************** RENDERING ******************************* */
	return (
		<Drawer
			classNames={classes}
			opened={opened}
			position="right"
			onClose={onClose}
			withCloseButton={false}
			size="544px"
		>
			{/* Header */}
			<Flex
				align="center"
				gap="16px"
				justify="space-between"
				pb={'var(--mantine-spacing-xl)'}
			>
				<Title
					order={6}
					c="var(--mantine-color-igluPrimary-9)"
				>
					{title}
				</Title>
				<ActionIcon
					color="var(--mantine-color-igluBorder-9)"
					radius={'48px'}
					variant="outline"
					size={'48px'}
					onClick={onClose}
				>
					<IconX
						color="var(--mantine-color-igluPrimary-9)"
						size={'24px'}
					/>
				</ActionIcon>
			</Flex>

			{/* Drawer content */}
			<Flex
				flex={1}
				direction={'column'}
				justify="space-between"
			>
				<Stack gap={'var(--mantine-spacing-sm)'}>
					{/* Datepicker filter */}
					<DatePickerInput
						onChange={(value) => {
							let body = {};
							body = {
								...(!isNil(value[0])
									? { 'query[createdAt][$gte]': value[0] }
									: {}),
								...(!isNil(value[1])
									? { 'query[createdAt][$lt]': value[1] }
									: {}),
							};

							setQueryParams((prev) => ({
								...prev,
								...body,
							}));
						}}
						size="md"
						placeholder="Filtrer par date"
						clearable
						type="range"
						presets={[
							{
								value: [
									dayjs()
										.startOf('week')
										.format('YYYY-MM-DD'),
									dayjs()
										.endOf('week')
										.format('YYYY-MM-DD'),
								],
								label: 'This week',
							},
							{
								value: [
									dayjs()
										.startOf('month')
										.format('YYYY-MM-DD'),
									dayjs()
										.endOf('month')
										.format('YYYY-MM-DD'),
								],
								label: 'This month',
							},
							{
								value: [
									dayjs()
										.startOf('year')
										.format('YYYY-MM-DD'),
									dayjs()
										.endOf('year')
										.format('YYYY-MM-DD'),
								],
								label: 'This year',
							},
						]}
						mb={'16px'}
						value={[
							queryParams['query[createdAt][$gte]']
								? dayjs(
										queryParams[
											'query[createdAt][$gte]'
										] as string
									).toDate()
								: null,
							queryParams['query[createdAt][$lt]']
								? dayjs(
										queryParams[
											'query[createdAt][$lt]'
										] as string
									).toDate()
								: null,
						]}
					/>

					<Stack>
						<Text
							c="var(--mantine-color-igluTextSecondary-9)"
							variant="button"
							mb={'20px'}
						>
							{t('global.availableStock')}
						</Text>
						<RangeSlider
							labelAlwaysOn
							color="var(--mantine-color-igluPrimary-9)"
							value={valueAvailableStock}
							min={0}
							max={20000}
							step={100}
							minRange={100}
							label={(value) =>
								`${value} ${getUnitLabel('tco2')}`
							}
							onChange={(value) => {
								if (value[0] == 0 && value[1] == 20000) {
									const cloneQueryParams =
										cloneDeep(queryParams);

									delete cloneQueryParams[
										'quantity.remaining_aco2'
									];
									setQueryParams(cloneQueryParams);
									return;
								}

								setQueryParams((prev) => ({
									...prev,
									'quantity.remaining_aco2': {
										$gte: value[0],
										$lte: value[1],
									},
								}));
								setValueAvailableStock(value);
							}}
						/>
					</Stack>
				</Stack>

				{/* Buttons */}
				<Flex
					direction={{
						base: 'column-reverse',
						sm: 'row',
					}}
					gap={{
						base: 'var(--mantine-spacing-sm)',
						sm: 'var(--mantine-spacing-lg)',
					}}
				>
					<Button
						fullWidth
						variant={'outline'}
						onClick={handleReset}
					>
						{t('global.resetFilters')}
					</Button>
					<Button
						onClick={handleApplyFilters}
						fullWidth
						variant={'primary'}
					>
						{t('global.applyFilters')}
					</Button>
				</Flex>
			</Flex>
		</Drawer>
	);
};
